import { CatalogTitle, fetchTitles } from '@/services/ContentCatalog'
import type { PageDataContext, PageModel } from '@/services/RenderService/types'
import {
  isCollectionDisplay,
  isContentExperimentModel,
  isCuratedCollection,
  isHasTypeName,
} from '@/services/RenderService/validations'
import { CollectionDisplay, Maybe, Page, PageContentItem, TitleCollection } from '@/types/codegen-contentful'
import { isStagingEnvironment } from '@/utils/environment-utils'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'

export function isRequiresTitleMapOnPage(page: Page) {
  return checkPageCollectionDisplay(page) || checkContentExperimentHasCollectionDisplayAndTitleCollection(page)
}

function checkPageCollectionDisplay(page: PageModel): boolean {
  return page.contentCollection?.items?.some(checkContentItemCollectionDisplayWithTitleCollection) ?? false
}

function checkContentItemCollectionDisplayWithTitleCollection(item: Maybe<PageContentItem>): boolean {
  if (!isCollectionDisplay(item)) return false
  return isTitleCollection(item.collection) || isCuratedCollection(item.curatedCollection)
}

function checkContentExperimentHasCollectionDisplayAndTitleCollection(page: PageModel): boolean {
  return (
    page.contentCollection?.items
      ?.filter(isContentExperimentModel)
      .flatMap((contentExperiment) => contentExperiment.variantsCollection?.items)
      .some((variant) =>
        variant?.contentItemsCollection?.items
          .filter(isDefined)
          .some((item) => checkContentItemCollectionDisplayWithTitleCollection(item)),
      ) ?? false
  )
}

export function isTitleCollection(content: unknown): content is TitleCollection {
  return isHasTypeName(content) && content.__typename === 'TitleCollection'
}

export async function fetchTitlesForPage(page: Page): Promise<CatalogTitle[]> {
  const ids = mapAllContentIds(page)
  const experimentIds = mapExperimentContentIds(page)
  const idsToFetch = ids.concat(experimentIds)

  const titles = await fetchTitles(
    // TODO: get a dynamic locale once pages or collections (to be designed) are regionalized
    'en',
    idsToFetch,
  )

  return titles
}

function mapAllContentIds(page: Page) {
  return (
    page.contentCollection?.items
      .filter(isCollectionDisplay)
      .map((collectionDisplay) => collectionDisplay.collection)
      .filter(isTitleCollection)
      .map((titleCollection) => titleCollection.contentCatalogIds)
      .flat()
      .filter(isDefined) || []
  )
}

function mapExperimentContentIds(page: Page) {
  return (
    page.contentCollection?.items
      .filter(isContentExperimentModel)
      .flatMap((contentExperiment) => contentExperiment.variantsCollection?.items)
      .flatMap((variant) => variant?.contentItemsCollection?.items)
      .filter((contentItem) => isCollectionDisplay(contentItem))
      .map((contentItem) => contentItem as CollectionDisplay)
      .filter(isDefined)
      .map((collectionDisplay) => collectionDisplay?.collection)
      .filter((collection) => isTitleCollection(collection))
      .map((collection) => collection?.contentCatalogIds)
      .filter(isDefined)
      .flat()
      .filter(isDefined) || []
  )
}

export function mapContentIdsToTitles(pageDataContext: PageDataContext, ids?: Maybe<Maybe<string>[]>): CatalogTitle[] {
  return (
    ids
      ?.map((id) => {
        if (!id) return undefined

        const titleMap = pageDataContext['title-map']
        if (!titleMap) {
          logger().error(`The "title-map" property is missing from the pageDataContext!`)
          return undefined
        }

        const catalogTitle = titleMap[id]
        if (!catalogTitle) {
          if (!isStagingEnvironment()) {
            logger().warn(`Expected to find a catalog title from the "title-map" by id, but found nothing!`, { id })
          }
          return undefined
        }

        return catalogTitle
      })
      .filter(isDefined) || []
  )
}
